import React, { useEffect, useState } from 'react';
import { Button, Loader } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useParams } from 'react-router';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useGetAllUsers } from 'routes/screens/settings/screens/user/hooks';
import EmptyState from 'shared/components/EmptyState';
import Icon from 'shared/components/Icon';
import Spacer from 'shared/components/Spacer';
import { useGetPlaceholderValues, useGetSurvey } from 'shared/hooks';
import { Center } from 'shared/styles';
import { AccountInfoType, UserType } from 'shared/types';
import ResponseSidebar from './components/ResponseSidebar';
import { SummaryPane } from './components/ResponseSidebar/styles';
import ResponseDetail from './components/response-detail';
import { useGetResponseNavigate } from '../../hooks';
import { ResponseContainer, TopBar } from '../../style';

export type ViewProps = {
  accountInfo?: AccountInfoType;
  userInfo?: UserType;
};

const ManageResponse = ({ accountInfo, userInfo }: ViewProps) => {
  const { t } = useTranslation();
  const params = useParams<{
    id: string;
    responseId: string;
  }>();
  const history = useHistory();
  const { state: locationState } = useLocation();
  const {
    selectedFilter: selectedFilter,
    surveyResponseCount: surveyResponseCount,
  } = (locationState as any) ?? {};

  const responseId = params.responseId;
  const currentSurvey = params?.id;
  const storedFilter = window?.localStorage?.getItem('response:filter')?.length
    ? JSON.parse(window?.localStorage?.getItem('response:filter') || '')
    : {
        filterParams: '',
        localState: {},
        isApply: false,
      };
  const [appliedFilters, setAppliedFilters] = useState<{
    filterParams: string;
    localState: any;
    isApply: boolean;
  }>(storedFilter);

  const { data: navigateData, isFetching } = useGetResponseNavigate({
    surveyId: currentSurvey,
    responseId,
    filterParams: appliedFilters?.filterParams ?? '',
  });
  const responseData = navigateData?.surveyResponses?.[0];

  const { data: placeholders = {}, isLoading: isPlaceholderLoading } =
    useGetPlaceholderValues(currentSurvey || '');
  const { data: users } = useGetAllUsers({});
  const {
    data: surveyInfo,
    refetch: refetchSurveyInfo,
    isFetching: isfetchingSurveyInfo,
  } = useGetSurvey({
    surveyId: currentSurvey || params?.id,
    versionId: responseData?.survey_version_id || '',
  });

  const handleNavigate = (type: string) => {
    const { next, prev } = navigateData?._links || {};
    const url = new URL(
      type === 'next'
        ? typeof next === 'object'
          ? next?.href || ''
          : ''
        : typeof prev === 'object'
        ? prev?.href || ''
        : '',
    );
    const paths = url.pathname.split('/');
    history.push(`/response/${currentSurvey}/${paths[paths.length - 2]}`, {
      surveyResponseCount,
    });
  };

  useEffect(() => {
    const storedFilter = window?.localStorage?.getItem('response:filter')
      ?.length
      ? JSON.parse(window?.localStorage?.getItem('response:filter') || '')
      : {
          filterParams: '',
          localState: {},
          isApply: false,
        };
    setAppliedFilters(storedFilter);
  }, []);

  if (!currentSurvey || (!responseData && !isFetching)) {
    return (
      <EmptyState
        title={`${t('empty_state.title.no_responses_here')}`}
        message={`${t('empty_state.message.invalid_response_url')}`}
        imgSrc="empty-state-response.svg"
      />
    );
  }

  useEffect(() => {
    !isFetching && refetchSurveyInfo();
  }, [navigateData]);

  const isLoaded =
    surveyInfo &&
    responseData &&
    !isPlaceholderLoading &&
    !isfetchingSurveyInfo;
  return (
    <>
      <TopBar detailView>
        <div className="left">
          <Spacer x={0.4} />

          {surveyInfo?.name ? (
            <div className="title">
              <Button
                type="secondary"
                inline
                aria-label="response back"
                onClick={() =>
                  history.push(`/response/${currentSurvey}`, { selectedFilter })
                }
              >
                <Icon glyph="arrow-left" width={12} height={10} />
              </Button>
              {selectedFilter?.name ? (
                <>
                  <h5 className="response-id">{surveyInfo.name}</h5>
                  <div style={{ margin: '0 5px' }}>
                    <Icon glyph="nextPageArrow" width={12} height={10} />
                  </div>
                  <h5 className="response-id">
                    {selectedFilter?.name}&nbsp;
                    {surveyResponseCount ? `(${surveyResponseCount})` : null}
                  </h5>
                </>
              ) : (
                <h5 className="response-id">
                  {surveyInfo.name}&nbsp;
                  {surveyResponseCount ? `(${surveyResponseCount})` : null}
                </h5>
              )}
              <div style={{ margin: '0 5px' }}>
                <Icon glyph="nextPageArrow" width={12} height={10} />
              </div>
              <h5 className="response-id">{`#RES${responseId}`}</h5>
            </div>
          ) : (
            <Skeleton height={24} width={250} />
          )}
        </div>
        <div className="right">
          <Link
            to={`/survey/${currentSurvey}/edit`}
            className="btn-survey-route"
            target="_blank"
          >
            <Icon fill="#12344d" width="12" height="12" glyph="open-new-tab" />
            {t('responses.table_header.goto_survey')}
          </Link>
          <div className="pagination-actions">
            <button
              aria-label={`${t('prev')}`}
              id="response-prev-btn"
              disabled={!navigateData?._links?.prev || !isLoaded}
              onClick={() => handleNavigate('prev')}
            >
              <Icon glyph="arrow-left" width={12} height={12} />
            </button>
            <div className="pagination-prev-next-container">
              <div className="pagination-text"> {t('prev')} </div>
              <div className="pagination-text"> {t('next')} </div>
            </div>
            <button
              aria-label={`${t('next')}`}
              id="response-next-btn"
              className="next-btn"
              disabled={!navigateData?._links?.next || !isLoaded}
              onClick={() => handleNavigate('next')}
            >
              <Icon glyph="arrow-right" width={12} height={12} />
            </button>
          </div>
        </div>
      </TopBar>
      <ResponseContainer>
        <ResponseDetail
          responseId={responseId}
          surveyInfo={surveyInfo}
          showContactSection={true}
          accountInfo={accountInfo}
          placeholders={placeholders}
          users={users}
          responseData={responseData}
          isResponseLoaded={!!isLoaded}
          userInfo={userInfo || {}}
        />
        <SummaryPane>
          {responseData?.id ? (
            <ResponseSidebar
              accountInfo={accountInfo}
              surveyResponse={responseData}
            />
          ) : (
            <Center>
              <Loader />
            </Center>
          )}
        </SummaryPane>
      </ResponseContainer>
    </>
  );
};

export default ManageResponse;
